import React, { useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import { useGamification } from '../../contexts/GamificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';

const { FiUsers, FiPieChart, FiBarChart2, FiAward, FiTarget, FiActivity } = FiIcons;

const CandidateStats = () => {
  const { candidates, interviews } = useData();
  const { leaderboard } = useGamification();

  // Merge candidate data with gamification points and interview stats
  const enhancedCandidates = useMemo(() => {
    return candidates.map(candidate => {
      // Find points from leaderboard (matching by name for this mock, normally ID)
      const gamificationData = leaderboard.find(l => l.name === candidate.name) || { points: 0, level: 1 };
      
      // Count interviews
      const candidateInterviews = interviews.filter(i => i.candidateId === candidate.id).length;

      return {
        ...candidate,
        points: gamificationData.points,
        level: gamificationData.level,
        interviewCount: candidateInterviews
      };
    }).sort((a, b) => b.points - a.points); // Sort by points desc
  }, [candidates, interviews, leaderboard]);

  // Calculate Aggregates
  const stats = useMemo(() => {
    const totalPoints = enhancedCandidates.reduce((acc, curr) => acc + curr.points, 0);
    const totalInterviews = enhancedCandidates.reduce((acc, curr) => acc + curr.interviewCount, 0);
    const avgPoints = enhancedCandidates.length ? Math.round(totalPoints / enhancedCandidates.length) : 0;
    
    // Group by Field
    const byField = enhancedCandidates.reduce((acc, curr) => {
      acc[curr.field] = (acc[curr.field] || 0) + 1;
      return acc;
    }, {});

    // Group by Experience
    const byExp = enhancedCandidates.reduce((acc, curr) => {
      acc[curr.experience] = (acc[curr.experience] || 0) + 1;
      return acc;
    }, {});

    return { totalPoints, totalInterviews, avgPoints, byField, byExp };
  }, [enhancedCandidates]);

  // Simple Bar Component
  const SimpleBar = ({ label, value, total, color = "bg-blue-500" }) => (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-900 font-bold">{value}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div 
          className={`h-2.5 rounded-full ${color}`} 
          style={{ width: `${(value / total) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4">
          <div className="bg-blue-100 p-3 rounded-full text-blue-600">
            <SafeIcon icon={FiUsers} className="text-xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Candidates</p>
            <h4 className="text-xl font-bold text-gray-900">{enhancedCandidates.length}</h4>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4">
          <div className="bg-green-100 p-3 rounded-full text-green-600">
            <SafeIcon icon={FiActivity} className="text-xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Interviews</p>
            <h4 className="text-xl font-bold text-gray-900">{stats.totalInterviews}</h4>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4">
          <div className="bg-purple-100 p-3 rounded-full text-purple-600">
            <SafeIcon icon={FiAward} className="text-xl" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Avg. Points/Candidate</p>
            <h4 className="text-xl font-bold text-gray-900">{stats.avgPoints}</h4>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Demographics by Field */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
        >
          <div className="flex items-center space-x-2 mb-6">
            <SafeIcon icon={FiPieChart} className="text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">Professional Fields</h3>
          </div>
          <div className="space-y-4">
            {Object.entries(stats.byField).map(([field, count]) => (
              <SimpleBar 
                key={field} 
                label={field} 
                value={count} 
                total={enhancedCandidates.length} 
                color="bg-blue-500"
              />
            ))}
          </div>
        </motion.div>

        {/* Demographics by Experience */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
        >
          <div className="flex items-center space-x-2 mb-6">
            <SafeIcon icon={FiBarChart2} className="text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900">Experience Levels</h3>
          </div>
          <div className="space-y-4">
            {Object.entries(stats.byExp).map(([exp, count]) => (
              <SimpleBar 
                key={exp} 
                label={exp} 
                value={count} 
                total={enhancedCandidates.length} 
                color="bg-green-500"
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Detailed Candidate List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Candidate Performance & Profiles</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Interviews</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {enhancedCandidates.map((candidate) => (
                <tr key={candidate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                        {candidate.name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                        <div className="text-xs text-gray-500">{candidate.city}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {candidate.field}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {candidate.experience}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <SafeIcon icon={FiTarget} className="mr-1 text-xs" />
                      {candidate.points.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 font-medium">
                    {candidate.interviewCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default CandidateStats;